/**
 * Settings → Language.
 *
 * Radio-style picker for the 3 supported locales. Selecting an option:
 *   1. Calls PATCH /me with { language } (optimistic via usePatchMePrefs)
 *   2. Calls i18n.changeLanguage(next) so the UI flips immediately
 *
 * If translations are not yet reviewer-approved (REVIEWED_LOCALES flag),
 * non-EN options render disabled with a "Soon" hint and EN is the only
 * functional choice. This is the safety-valve documented in tasks.md T30/T31
 * (Risks §1).
 */

import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { useMe, type LanguageCode } from '../../hooks/useMe';
import { usePatchMePrefs } from '../../hooks/usePatchMePrefs';
import { SUPPORTED_LOCALES } from '../../i18n/index';
import styles from './Language.module.css';

const REVIEWED_LOCALES: LanguageCode[] = ['en', 'ru', 'uk'];

export function Language(): JSX.Element {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { data: me, isLoading, isError } = useMe();
  const patch = usePatchMePrefs();

  const current = (me?.language ?? (i18n.language as LanguageCode)) as LanguageCode;

  const onPick = (next: LanguageCode): void => {
    if (next === current) return;
    if (!REVIEWED_LOCALES.includes(next)) return;
    void i18n.changeLanguage(next);
    patch.mutate({ language: next });
  };

  return (
    <MeSubpageShell title={t('language.title', { ns: 'settings' })} data-testid="settings-language-page">
      {isLoading && <p data-testid="language-loading">{t('states.loading', { ns: 'common' })}</p>}
      {isError && (
        <p role="alert" data-testid="language-error">{t('account.loadError', { ns: 'settings' })}</p>
      )}
      {!isLoading && !isError && (
        <section className={styles.section} aria-label={t('language.title', { ns: 'settings' })}>
          {SUPPORTED_LOCALES.map((loc) => {
            const reviewed = REVIEWED_LOCALES.includes(loc);
            const checked = current === loc;
            return (
              <button
                key={loc}
                type="button"
                role="radio"
                aria-checked={checked}
                className={styles.row}
                disabled={!reviewed}
                onClick={() => onPick(loc)}
                data-testid={`language-option-${loc}`}
              >
                <div>
                  <p className={styles.rowLabel}>{t(`language.options.${loc}`, { ns: 'settings' })}</p>
                  {!reviewed && (
                    <p className={styles.rowHint} data-testid={`language-soon-${loc}`}>
                      {t('language.soon', { ns: 'settings' })}
                    </p>
                  )}
                </div>
                <span className={styles.check} data-checked={checked} />
              </button>
            );
          })}
          {patch.isError && (
            <p role="alert" data-testid="language-save-error">
              {t('language.saveError', { ns: 'settings' })}
            </p>
          )}
        </section>
      )}
    </MeSubpageShell>
  );
}
